// Documentos da matrícula · visão da FAMÍLIA (APP + DNA Connect Web).
// Nada aqui bloqueia a matrícula: o que falta fica como pendência acompanhada.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, Clock, FileText, Loader2, RefreshCw, Upload, XCircle, AlertTriangle, Layers } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const P = () => ({ params: { token: localStorage.getItem('familyToken') || localStorage.getItem('dnaconnect_token') || '' } });

const BADGE = {
  aprovado: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  recebido_fisico: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  enviado: { cls: 'bg-blue-50 text-blue-700 border-blue-200', icon: Clock },
  em_analise: { cls: 'bg-blue-50 text-blue-700 border-blue-200', icon: Clock },
  entrega_posterior: { cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
  pendente: { cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: AlertTriangle },
  rejeitado: { cls: 'bg-rose-50 text-rose-700 border-rose-200', icon: XCircle },
  nao_enviado: { cls: 'bg-slate-100 text-slate-500 border-slate-200', icon: Upload },
};

export default function DocumentosMatricula({ processId, studentName }) {
  const [data, setData] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const inputs = useRef({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/dnaconnect/matriculas/${processId}/documentos`, P());
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Não foi possível carregar os documentos.');
    }
  }, [processId]);

  useEffect(() => { load(); }, [load]);

  const send = async (key, picked) => {
    if (!picked?.length) return;
    setBusyKey(key);
    try {
      const fd = new FormData();
      picked.forEach((f) => fd.append('files', f));
      const url = key === '__multi__'
        ? `/dnaconnect/matriculas/${processId}/documentos-multiplos`
        : `/dnaconnect/matriculas/${processId}/documentos/${key}`;
      await api.post(url, fd, { ...P(), headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 });
      toast.success('Recebemos! A Secretaria vai conferir.');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha no envio. Tente novamente.');
    } finally { setBusyKey(null); }
  };

  const reutilizar = async () => {
    setBusyKey('docs_responsaveis');
    try {
      const { data } = await api.post(`/dnaconnect/matriculas/${processId}/documentos/docs_responsaveis/reutilizar`, null, P());
      toast.success(data.message);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Não foi possível reutilizar.');
    } finally { setBusyKey(null); }
  };

  if (!data) return <div className="grid min-h-[30vh] place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#64327D]" /></div>;

  const s = data.summary;
  const groups = ['aluno', 'familia', 'outros'];

  return (
    <div data-testid="familia-documentos">
      <div className="rounded-2xl bg-white p-4 shadow-sm">
        <p className="text-[11px] uppercase tracking-wide text-violet-900/50">Documentação</p>
        <h2 className="text-lg font-semibold text-violet-950">{data.headline}</h2>
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-violet-100">
          <div className="h-full bg-[#64327D] transition-all"
            style={{ width: `${Math.round((s.received / Math.max(1, s.total)) * 100)}%` }} />
        </div>
        <div className="mt-2 flex flex-wrap gap-2 text-[12px]">
          <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-emerald-700">✓ {s.received} enviados</span>
          <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-slate-600">○ {s.missing_required} faltando</span>
          <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-700">⏳ {s.deferred} podem ser entregues depois</span>
        </div>
        <p className="mt-2 text-[12px] leading-relaxed text-violet-900/70">{data.message}</p>

        <label className="mt-3 flex h-11 cursor-pointer items-center justify-center gap-2 rounded-xl border border-violet-200 text-[13px] font-medium text-violet-800">
          <Layers className="h-4 w-4" /> Enviar vários documentos
          <input type="file" multiple accept="image/*,application/pdf,.heic" className="hidden"
            data-testid="doc-input-multi"
            onChange={(e) => { const arr = Array.from(e.target.files || []); e.target.value = ''; send('__multi__', arr); }} />
        </label>
      </div>

      {groups.map((g) => {
        const items = (data.items || []).filter((i) => i.group === g);
        if (!items.length) return null;
        return (
          <div key={g} className="mt-3 rounded-2xl bg-white p-4 shadow-sm">
            <p className="text-[11px] uppercase tracking-wide text-violet-900/50">{items[0].group_label}</p>
            <div className="mt-2 divide-y divide-violet-50">
              {items.map((i) => {
                const b = BADGE[i.status] || BADGE.nao_enviado;
                const Icon = b.icon;
                const sent = ['aprovado', 'recebido_fisico', 'enviado', 'em_analise'].includes(i.status);
                return (
                  <div key={i.key} className="py-3" data-testid={`doc-row-${i.key}`}>
                    <div className="flex items-start gap-2">
                      <Icon className={`mt-0.5 h-4 w-4 flex-shrink-0 ${sent ? 'text-emerald-600' : i.status === 'rejeitado' ? 'text-rose-600' : 'text-violet-400'}`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-[13.5px] font-medium text-violet-950">{i.label}</p>
                        <span className={`mt-1 inline-block rounded-full border px-2 py-0.5 text-[11px] ${b.cls}`}>
                          {i.status_label}
                        </span>
                        {i.rejection_reason && (
                          <p className="mt-1 text-[12px] text-rose-700">{i.rejection_reason}</p>
                        )}
                        {!sent && i.hint && <p className="mt-1 text-[11.5px] text-violet-900/50">{i.hint}</p>}
                        {!!i.files?.length && (
                          <p className="mt-1 text-[11.5px] text-violet-900/50">{i.files.length} arquivo(s) enviado(s)</p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {i.can_replace && (
                          <label className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-full bg-[#64327D] px-3 text-[12px] font-semibold text-white">
                            {busyKey === i.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : sent ? <RefreshCw className="h-3.5 w-3.5" /> : <Upload className="h-3.5 w-3.5" />}
                            {sent ? 'Substituir' : i.deferrable ? 'Enviar agora' : 'Enviar'}
                            <input ref={(el) => { inputs.current[i.key] = el; }} type="file" multiple
                              accept="image/*,application/pdf,.heic" className="hidden"
                              data-testid={`doc-input-${i.key}`}
                              onChange={(e) => { const arr = Array.from(e.target.files || []); e.target.value = ''; send(i.key, arr); }} />
                          </label>
                        )}
                        {i.key === 'docs_responsaveis' && data.reusable_guardian_docs && i.status === 'nao_enviado' && (
                          <button onClick={reutilizar} data-testid="doc-reuse-guardian"
                            className="text-[11.5px] text-violet-700 underline">
                            Usar documento já cadastrado
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <p className="mt-3 flex items-center justify-center gap-1.5 text-center text-[11.5px] text-violet-900/50">
        <FileText className="h-3.5 w-3.5" /> Seus documentos ficam guardados com segurança para a Secretaria.
      </p>
    </div>
  );
}
